import { z } from 'zod';

import {
  PaymentMethodType,
  PaymentStatus,
  SubscriptionLetterSignatureStatus,
} from '@/domain/project/services/invest/type';
import {
  ApiPendingCampaign,
  ApiPortfolioInstanciatedInvestment,
  ApiValidatedInvestment,
} from '@/lib/core-api/types';

export const userSchema = z.object({
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  email: z.string().optional(),
  profilePicture: z.string().optional(),
  isVerified: z.boolean(),
  walletAmount: z.number().optional(),
  isCompany: z.boolean().optional(),
  isCGP: z.boolean().optional(), // gestionnaire en gestion de partimoine
  isIdentityValidated: z.boolean().optional(), // for old users that have been validated before but are missing new mendatory information
  isFolderCompleted: z.boolean().optional(), // for old users that have been validated before but did not fill every new quizz question in
  hasAlreadyInvested: z.boolean().optional(),
});

export type User = z.infer<typeof userSchema>;

export enum CampaignStatus {
  Invisible = 'invisible',
  Study = 'study',
  Visible = 'visible',
  Closed = 'closed',
  NonFunded = 'nonFunded',
  Funded = 'funded',
  Abandoned = 'abandoned',
  PositiveOuted = 'positiveOuted',
  NegativeOuted = 'negativeOuted',
}

export const mapCampaignStatusWithColor = {
  [CampaignStatus.Abandoned]: 'hsl(var(--muted-foreground))',
  [CampaignStatus.Closed]: 'hsl(var(--accent-foreground))',
  [CampaignStatus.Funded]: 'hsl(var(--muted-success))',
  [CampaignStatus.NegativeOuted]: 'hsl(var(--purple))',
  [CampaignStatus.NonFunded]: 'hsl(var(--accent-foreground))',
  [CampaignStatus.PositiveOuted]: 'hsl(var(--olive))',
  [CampaignStatus.Invisible]: 'hsl(var(--muted-foreground))',
  [CampaignStatus.Study]: 'hsl(var(--muted-foreground))',
  [CampaignStatus.Visible]: 'hsl(var(--muted-foreground))',
};

export const endedCampaignStatus = [
  CampaignStatus.Closed,
  CampaignStatus.NonFunded,
  CampaignStatus.Funded,
  CampaignStatus.Abandoned,
  CampaignStatus.PositiveOuted,
  CampaignStatus.NegativeOuted,
];

export enum CompanyStatus {
  PendingCampaign = 'pendingCampaign',
  PendingClosing = 'pendingClosing',
  InitialisedInvestment = 'initialisedInvestment',
  PendingBankruptcy = 'pendingBankruptcy',
  TargetSocietyPendingBankruptcy = 'targetSocietyPendingBankruptcy',
  Bankrupt = 'bankrupt',
  TargetSocietyBankrupt = 'targetSocietyBankrupt',
  PendingHoldingSocietyTermination = 'pendingHoldingSocietyTermination',
  TerminatedHoldingSociety = 'terminatedHoldingSociety',
  PendingOutingNegociation = 'pendingOutingNegociation',
  TerminationOfPendingActions = 'terminationOfPendingActions',
  TerminationOfRealisedActions = 'terminationOfRealisedActions',
  RealisedRefund = 'realisedRefund',
}

export const mapCompanyStatusWithColor = {
  [CompanyStatus.PendingCampaign]: 'hsl(var(--muted-foreground))',
  [CompanyStatus.PendingClosing]: 'hsl(var(--primary))',
  [CompanyStatus.InitialisedInvestment]: 'hsl(var(--secondary))',
  [CompanyStatus.PendingBankruptcy]: 'hsl(var(--complementary))',
  [CompanyStatus.TargetSocietyPendingBankruptcy]: 'hsl(var(--complementary))',
  [CompanyStatus.Bankrupt]: 'hsl(var(--complementary))',
  [CompanyStatus.TargetSocietyBankrupt]: 'hsl(var(--complementary))',
  [CompanyStatus.PendingHoldingSocietyTermination]: 'hsl(var(--complementary))',
  [CompanyStatus.TerminatedHoldingSociety]: 'hsl(var(--complementary))',
  [CompanyStatus.PendingOutingNegociation]: 'hsl(var(--secondary))',
  [CompanyStatus.TerminationOfPendingActions]: 'hsl(var(--olive))',
  [CompanyStatus.TerminationOfRealisedActions]: 'hsl(var(--purple))',
  [CompanyStatus.RealisedRefund]: 'hsl(var(--alt-background))',
};

export enum TransactionKind {
  MoneyIn = 'money-in',
  MoneyOut = 'money-out',
  Investment = 'investment',
}

export type TransactionType = 'investment' | 'wallet';

export type WaitingForSubscriptionInvestment = Omit<
  ApiPortfolioInstanciatedInvestment,
  'campaignStatus' | 'paymentStatus'
> & {
  campaignStatus: CampaignStatus;
  paymentStatus: PaymentStatus;
};

export type PendingInvestment = Omit<
  ApiPortfolioInstanciatedInvestment,
  'campaignStatus' | 'paymentStatus' | 'paymentMethod'
> & {
  campaignStatus: CampaignStatus;
  paymentStatus: PaymentStatus;
  paymentMethod?: PaymentMethodType;
};

export type PendingCampaign = Omit<ApiPendingCampaign, 'campaignStatus'> & {
  campaignStatus: CampaignStatus;
};

export type ValidatedInvestment = Omit<
  ApiValidatedInvestment,
  'companyStatus' | 'companyBackgroundImage'
> & {
  companyStatus: CompanyStatus;
  companyBackgroundImage?: string;
};

export type Portfolio = {
  waitingForSubscriptionInvestments: WaitingForSubscriptionInvestment[];
  pendingInvestments: PendingInvestment[];
  pendingCampaigns: PendingCampaign[];
  validatedInvestments: ValidatedInvestment[];
};

export type SocialAuth = {
  label: string;
  logo: string;
  key: string;
};

export type Transaction = {
  amount: number;
  date: Date;
  destination: string;
  id: string;
  color: string;
  status: PaymentStatus;
  paymentMode?: PaymentMethodType;
};

export type DetailedTransaction = {
  id: string;
  project?: {
    id: number;
    slug: string;
    campaignName: string;
  };
  amount?: number;
  investmentVehicle?: {
    id: number;
    label: string;
  };
  subscriptionLetterId?: string;
  subscriptionLetterSignatureStatus?: SubscriptionLetterSignatureStatus;
  paymentMethod?: PaymentMethodType;
  paymentStatus: PaymentStatus;
  fee?: number;
  statusUpdatedAt?: Date;
};
